// React
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect as reduxConnect } from 'react-redux'

// Components
import Select from 'ui/components/Select'
import { components } from 'react-select'

// Libraries

// Shared
import moment from 'shared/lib/moment'

class TimeSelect extends Component {
  static displayName = 'TimeSelect'

  static propTypes = {
    value: PropTypes.any,
    intervalInMinutes: PropTypes.number,
    format: PropTypes.string,
    currentSelection: PropTypes.string,
    disabledTimes: PropTypes.shape({
      before: PropTypes.any,
      after: PropTypes.any
    }),
    isTimeSelectable: PropTypes.func,
    useAMOrPM: PropTypes.bool,
    everythingIsSelectable: PropTypes.bool,
    disabled: PropTypes.bool,
    field: PropTypes.shape({
      value: PropTypes.any,
      onChange: PropTypes.func
    }),
    operatingRules: PropTypes.any
  }

  static defaultProps = {
    intervalInMinutes: 15
  }

  state = {
    selectOptions: []
  }

  Option = (props) => {
    const isAvailable = props.data.available !== false
    const dotColor = isAvailable ? '#22C55E' : '#EF4444'
    const status = isAvailable ? 'available' : 'blocked'

    return (
      <components.Option {...props}>
        <div
          style={{ display: 'flex', alignItems: 'center', marginLeft: 12 }}
          data-tid={`time-option-${props.data.value}`}
        >
          <span
            data-tid={`time-indicator-${status}`}
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: dotColor,
              marginRight: 12
            }}
          />
          <span>{props.children}</span>
        </div>
      </components.Option>
    )
  }

  componentDidMount = () => {
    this.selectOptions()
  }

  componentDidUpdate = (prevProps) => {
    const { currentSelection, intervalInMinutes } = this.props

    // Picker value changed, recalculate options
    if (
      prevProps[currentSelection]?.getTime() !== this.props[currentSelection]?.getTime() ||
      prevProps.intervalInMinutes !== intervalInMinutes ||
      prevProps.operatingRules !== this.props.operatingRules ||
      prevProps.disabled !== this.props.disabled ||
      prevProps.isTimeSelectable !== this.props.isTimeSelectable
    ) {
      this.selectOptions()
    }
  }

  get forcedValue () {
    const { field } = this.props

    if (!field.value) return undefined

    return {
      label: this.getLabel(field.value),
      value: field.value
    }
  }

  get momentFormat () {
    return this.props.useAMOrPM ? 'hh:mm A' : 'HH:mm'
  }

  getLabel = (date) => {
    if (!moment.isMoment(date)) date = moment(date, 'HH:mm')

    return date.format(this.momentFormat)
  }

  resolveAvailability = (value) => {
    const { isTimeSelectable } = this.props

    if (typeof isTimeSelectable === 'function') {
      return Boolean(isTimeSelectable(value))
    }

    return true
  }

  selectOptions = () => {
    const { disabledTimes, disabled, field } = this.props
    const options = []

    if (disabled && field?.value) {
      // Field is disabled but has a value
      // Add value to options so it gets displayed
      options.push(this.forcedValue)

      this.setState({ selectOptions: options })
    }

    if (disabled) return options

    for (let hour = 0; hour < 24; hour++) {
      for (let minutes = 0; minutes < 60; minutes = minutes + this.props.intervalInMinutes) {
        const time = moment().hour(hour).minute(minutes)

        if (disabledTimes && disabledTimes.before) {
          const time = moment(disabledTimes.before).hour(hour).minute(minutes)
          const beforeTime = moment(disabledTimes.before)

          if (time < beforeTime) {
            continue
          }
        }

        if (disabledTimes && disabledTimes.after) {
          const time = moment(disabledTimes.after).hour(hour).minute(minutes)
          const afterTime = moment(disabledTimes.after)

          if (time > afterTime) {
            continue
          }
        }

        const label = this.getLabel(time)
        const value = time.format('HH:mm')

        // Mark availability for potential custom rendering
        const available = this.resolveAvailability(value)

        // Always include option; color will indicate availability
        options.push({ label, value, available })
      }
    }

    this.setState({ selectOptions: options })
  }

  render () {
    const selectProps = {}

    const isNotDefaultInterval = this.props.intervalInMinutes !== 15

    if (isNotDefaultInterval) {
      selectProps.forceValue = true
      selectProps.value = this.forcedValue
    }

    return (
      <Select
        {...this.props}
        {...selectProps}
        options={this.state.selectOptions}
        components={{ Option: this.Option }}
      />
    )
  }
}

const mapStateToProps = (state) => {
  return {
    operatingRules: state.orm.operating_rules,
    useAMOrPM: state.settings.get('dates.use_am_pm')
  }
}

export default reduxConnect(mapStateToProps)(TimeSelect)
